package se.mittpass.web.pages

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.*
import se.mittpass.shared.api.*

private val fsScope = MainScope()
private val CAT_LABELS = mapOf("temperature" to "Temperatur", "cleaning" to "Rengöring", "receiving" to "Mottagning", "other" to "Övrigt")
private val FREQ_LABELS = mapOf("daily" to "Dagligen", "per_delivery" to "Per leverans", "weekly" to "Veckovis")

private enum class FsTab(val label: String, val icon: String, val managerOnly: Boolean = false) {
    KONTROLL("Dagens kontroll", "fa-clipboard-check"),
    TEMPLATES("Kontrollpunkter", "fa-sliders", true),
    LOGG("Logg & Avvikelser", "fa-clock-rotate-left", true)
}

private val QUICK_TEMPLATES = listOf(
    CreateFoodSafetyTemplateRequest("Kyl 1", "temperature", "C", 0.0, 8.0, "daily", 1),
    CreateFoodSafetyTemplateRequest("Kyl 2", "temperature", "C", 0.0, 8.0, "daily", 2),
    CreateFoodSafetyTemplateRequest("Frys", "temperature", "C", -25.0, -18.0, "daily", 3),
    CreateFoodSafetyTemplateRequest("Leverans kylvaror", "temperature", "C", 0.0, 8.0, "per_delivery", 4),
    CreateFoodSafetyTemplateRequest("Leverans frysvaror", "temperature", "C", -25.0, -15.0, "per_delivery", 5),
    CreateFoodSafetyTemplateRequest("Varmhållning", "temperature", "C", 60.0, 100.0, "daily", 6),
    CreateFoodSafetyTemplateRequest("Nedkylning (2h)", "temperature", "C", 0.0, 8.0, "daily", 7),
    CreateFoodSafetyTemplateRequest("Städkontroll", "cleaning", "boolean", null, null, "daily", 8),
    CreateFoodSafetyTemplateRequest("Handtvättkontroll", "cleaning", "boolean", null, null, "daily", 9)
)

@Composable
fun FoodSafetyPage(workplaceId: Long, isManager: Boolean) {
    var currentFsTab by remember { mutableStateOf(FsTab.KONTROLL) }
    var todayItems by remember { mutableStateOf<List<FoodSafetyTodayItemDto>>(emptyList()) }
    var templates by remember { mutableStateOf<List<FoodSafetyTemplateDto>>(emptyList()) }
    var logs by remember { mutableStateOf<List<FoodSafetyLogDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var feedbackMsg by remember { mutableStateOf<String?>(null) }
    var showCreate by remember { mutableStateOf(false) }
    var logFrom by remember { mutableStateOf(addFsDays(-7)) }
    var logTo by remember { mutableStateOf(todayFs()) }

    fun loadToday() { fsScope.launch { foodSafetyRepo.getToday(workplaceId).onSuccess { todayItems = it }; loading = false } }
    fun loadTemplates() { fsScope.launch { foodSafetyRepo.getTemplates(workplaceId).onSuccess { templates = it } } }
    fun loadLogs() { fsScope.launch { foodSafetyRepo.getLogs(workplaceId, logFrom, logTo).onSuccess { logs = it } } }

    LaunchedEffect(workplaceId) { loadToday(); loadTemplates() }

    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1.25rem") } }) {
        feedbackMsg?.let { msg ->
            Div({
                style {
                    property("background", "var(--rp-ok-bg)")
                    property("color", "var(--rp-ok-text)")
                    property("padding", "0.85rem 1.25rem")
                    property("border-radius", "16px")
                    property("font-weight", "700")
                    property("font-size", "0.9rem")
                    property("box-shadow", "0 4px 12px rgba(16, 185, 129, 0.12)")
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "0.5rem")
                }
            }) {
                FaIcon("fa-solid fa-circle-check", size = "1rem", color = "var(--rp-ok-text)")
                Text(msg)
            }
        }

        // Modern Clean Header Card
        Div({
            style {
                property("background", "var(--rp-surface)")
                property("border-radius", "20px")
                property("padding", "1.25rem 1.5rem")
                property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.03)")
                property("border", "1px solid var(--rp-hl-med)")
                property("display", "flex")
                property("justify-content", "space-between")
                property("align-items", "center")
                property("flex-wrap", "wrap")
                property("gap", "1rem")
            }
        }) {
            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "1rem") } }) {
                Div({
                    style {
                        property("width", "44px")
                        property("height", "44px")
                        property("border-radius", "12px")
                        property("background", "var(--rp-info-bg)")
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("flex-shrink", "0")
                    }
                }) {
                    FaIcon("fa-solid fa-shield-halved", size = "1.2rem", color = "var(--rp-pine)")
                }
                Div {
                    H3({ style { property("margin", "0 0 0.2rem 0"); property("font-size", "1.3rem"); property("font-weight", "800"); property("color", "var(--rp-text)"); property("letter-spacing", "-0.3px") } }) {
                        Text("Egenkontroll & Livsmedelssäkerhet")
                    }
                    P({ style { property("margin", "0"); property("color", "var(--rp-subtle)"); property("font-size", "0.84rem"); property("font-weight", "500") } }) {
                        Text("HACCP-efterlevnad, temperaturer och hygienkontroller")
                    }
                }
            }

            // Sub-tabs Segmented Pill Navigation
            Div({
                style {
                    property("display", "flex")
                    property("background", "var(--rp-overlay)")
                    property("border", "1px solid var(--rp-hl-med)")
                    property("padding", "3px")
                    property("border-radius", "12px")
                    property("gap", "3px")
                }
            }) {
                FsTab.values().filter { !it.managerOnly || isManager }.forEach { tab ->
                    val active = currentFsTab == tab
                    Button({
                        onClick { currentFsTab = tab; if (tab == FsTab.LOGG) loadLogs() }
                        style {
                            property("padding", "0.5rem 1rem")
                            property("border-radius", "9px")
                            property("border", "none")
                            property("font-weight", if (active) "800" else "600")
                            property("font-size", "0.82rem")
                            property("cursor", "pointer")
                            property("transition", "all 0.15s ease")
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "0.45rem")
                            property("font-family", "inherit")
                            if (active) {
                                property("background", "var(--rp-surface)")
                                property("color", "var(--rp-text)")
                                property("box-shadow", "0 1px 4px rgba(0, 0, 0, 0.08)")
                            } else {
                                property("background", "transparent")
                                property("color", "var(--rp-subtle)")
                            }
                        }
                    }) {
                        FaIcon("fa-solid ${tab.icon}", size = "0.82rem", color = if (active) "var(--rp-pine)" else "var(--rp-subtle)")
                        Text(tab.label)
                    }
                }
            }
        }

        when (currentFsTab) {
            FsTab.KONTROLL -> {
                if (loading) {
                    Div({ style { property("text-align", "center"); property("padding", "3rem"); property("color", "var(--rp-subtle)"); property("font-weight", "600") } }) {
                        FaIcon("fa-solid fa-spinner fa-spin", size = "1.5rem", color = "var(--rp-pine)")
                        Br(); Br()
                        Text("Laddar kontrollpunkter…")
                    }
                    return@Div
                }
                if (todayItems.isEmpty()) {
                    Div({
                        style {
                            property("background", "var(--rp-surface)")
                            property("border-radius", "24px")
                            property("padding", "3rem 2rem")
                            property("text-align", "center")
                            property("border", "1px solid var(--rp-hl-med)")
                        }
                    }) {
                        FaIcon("fa-solid fa-clipboard-list", size = "2.5rem", color = "var(--rp-muted)")
                        H4({ style { property("margin", "1rem 0 0.5rem 0"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                            Text("Inga kontrollpunkter konfigurerade än")
                        }
                        P({ style { property("margin", "0"); property("color", "var(--rp-subtle)"); property("font-size", "0.9rem") } }) {
                            Text("Skapa kontrollpunkter under \"Kontrollpunkter\" för att påbörja daily HACCP-loggning.")
                        }
                    }
                    return@Div
                }

                val grouped = todayItems.groupBy { it.template.category }
                listOf("temperature", "cleaning", "receiving", "other").forEach { cat ->
                    val items = grouped[cat] ?: return@forEach
                    val catBadgeBg = when (cat) {
                        "temperature" -> "var(--rp-info-bg)"
                        "cleaning" -> "var(--rp-ok-bg)"
                        "receiving" -> "var(--rp-info-bg)"
                        else -> "var(--rp-overlay)"
                    }
                    val catBadgeFg = when (cat) {
                        "temperature" -> "var(--rp-pine)"
                        "cleaning" -> "var(--rp-ok-text)"
                        "receiving" -> "var(--rp-iris)"
                        else -> "var(--rp-subtle)"
                    }
                    val catIcon = when (cat) {
                        "temperature" -> "fa-temperature-half"
                        "cleaning" -> "fa-spray-can-sparkles"
                        "receiving" -> "fa-box"
                        else -> "fa-list-check"
                    }

                    val isAllDone = items.all { it.completedToday }

                    Div({
                        style {
                            property("background", "var(--rp-surface)")
                            property("border-radius", "20px")
                            property("padding", "1.35rem 1.5rem")
                            property("box-shadow", "0 2px 8px rgba(0,0,0,0.03)")
                            property("border", "1px solid var(--rp-hl-med)")
                        }
                    }) {
                        Div({
                            style {
                                property("display", "flex")
                                property("align-items", "center")
                                property("justify-content", "space-between")
                                property("gap", "0.75rem")
                                property("margin-bottom", "1.1rem")
                            }
                        }) {
                            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.65rem") } }) {
                                Div({
                                    style {
                                        property("width", "36px")
                                        property("height", "36px")
                                        property("border-radius", "10px")
                                        property("background", catBadgeBg)
                                        property("display", "flex")
                                        property("align-items", "center")
                                        property("justify-content", "center")
                                        property("flex-shrink", "0")
                                    }
                                }) {
                                    FaIcon("fa-solid $catIcon", size = "0.95rem", color = catBadgeFg)
                                }
                                H4({
                                    style {
                                        property("margin", "0")
                                        property("font-size", "1.1rem")
                                        property("font-weight", "800")
                                        property("color", "var(--rp-text)")
                                        property("letter-spacing", "-0.2px")
                                    }
                                }) {
                                    Text(CAT_LABELS[cat] ?: cat)
                                }
                            }

                            // Completion Pill Tag
                            Span({
                                style {
                                    property("background", if (isAllDone) "var(--rp-ok-bg)" else "var(--rp-overlay)")
                                    property("color", if (isAllDone) "var(--rp-ok-text)" else "var(--rp-subtle)")
                                    property("border", if (isAllDone) "1px solid var(--rp-ok-border)" else "1px solid var(--rp-hl-med)")
                                    property("padding", "0.25rem 0.65rem")
                                    property("border-radius", "50px")
                                    property("font-size", "0.74rem")
                                    property("font-weight", "800")
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("gap", "0.35rem")
                                }
                            }) {
                                if (isAllDone) {
                                    FaIcon("fa-solid fa-check", size = "0.7rem", color = "var(--rp-ok-text)")
                                }
                                val doneCount = items.count { it.completedToday }
                                Text("$doneCount / ${items.size} Utförda")
                            }
                        }
                        Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "0.65rem") } }) {
                            items.forEach { item ->
                                FoodSafetyCheckItem(item, workplaceId) { loadToday(); feedbackMsg = "Registrerad" }
                            }
                        }
                    }
                }
            }

            FsTab.TEMPLATES -> {
                Div({
                    style {
                        property("background", "var(--rp-surface)")
                        property("border-radius", "24px")
                        property("padding", "1.5rem")
                        property("border", "1px solid var(--rp-hl-med)")
                        property("box-shadow", "0 4px 15px -2px rgba(0,0,0,0.03)")
                    }
                }) {
                    Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("align-items", "center"); property("margin-bottom", "1.25rem") } }) {
                        Div {
                            H4({ style { property("margin", "0 0 0.25rem 0"); property("font-weight", "800"); property("font-size", "1.15rem"); property("color", "var(--rp-text)") } }) { Text("Kontrollpunkter & Regler") }
                            P({ style { property("margin", "0"); property("color", "var(--rp-subtle)"); property("font-size", "0.85rem") } }) { Text("Konfigurera gränsvärden och frekvenser för din egenkontroll") }
                        }
                        Button({
                            onClick { showCreate = true }
                            style {
                                property("padding", "0.6rem 1.25rem")
                                property("border", "none")
                                property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
                                property("color", "white")
                                property("border-radius", "50px")
                                property("font-weight", "700")
                                property("font-size", "0.85rem")
                                property("cursor", "pointer")
                                property("box-shadow", "0 4px 12px rgba(108, 60, 225, 0.25)")
                                property("display", "flex")
                                property("align-items", "center")
                                property("gap", "0.4rem")
                            }
                        }) {
                            FaIcon("fa-solid fa-plus", size = "0.85rem", color = "white")
                            Text("Ny kontrollpunkt")
                        }
                    }

                    if (templates.isEmpty()) {
                        // Quick-start card
                        Div({
                            style {
                                property("background", "var(--rp-overlay)")
                                property("border-radius", "20px")
                                property("padding", "2rem")
                                property("text-align", "center")
                                property("border", "1px solid var(--rp-info-border)")
                            }
                        }) {
                            FaIcon("fa-solid fa-wand-magic-sparkles", size = "2rem", color = "var(--rp-pine)")
                            H4({ style { property("margin", "1rem 0 0.5rem 0"); property("color", "var(--rp-pine)"); property("font-weight", "800"); property("font-size", "1.1rem") } }) {
                                Text("Snabbstart för Restaurang & Café")
                            }
                            P({ style { property("color", "var(--rp-iris)"); property("font-size", "0.9rem"); property("margin", "0 0 1.25rem 0"); property("max-width", "500px"); property("margin-left", "auto"); property("margin-right", "auto") } }) {
                                Text("Skapa vanliga kontrollpunkter enligt Livsmedelsverkets krav (Kyl, Frys, Varmhållning, Städkontroll) med ett klick.")
                            }
                            Button({
                                onClick {
                                    fsScope.launch {
                                        QUICK_TEMPLATES.forEach { foodSafetyRepo.createTemplate(workplaceId, it) }
                                        loadTemplates(); loadToday()
                                        feedbackMsg = "✓ ${QUICK_TEMPLATES.size} standardkontroller skapade"
                                    }
                                }
                                style {
                                    property("padding", "0.75rem 1.75rem")
                                    property("border", "none")
                                    property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-foam))")
                                    property("color", "white")
                                    property("border-radius", "50px")
                                    property("font-weight", "700")
                                    property("font-size", "0.9rem")
                                    property("cursor", "pointer")
                                    property("box-shadow", "0 4px 14px rgba(0, 201, 167, 0.3)")
                                }
                            }) { Text("Skapa 9 standardkontroller") }
                        }
                    } else {
                        templates.forEach { tmpl ->
                            val catBadgeBg = when (tmpl.category) {
                                "temperature" -> "var(--rp-info-bg)"
                                "cleaning" -> "var(--rp-ok-bg)"
                                "receiving" -> "var(--rp-info-bg)"
                                else -> "var(--rp-overlay)"
                            }
                            val catBadgeFg = when (tmpl.category) {
                                "temperature" -> "var(--rp-pine)"
                                "cleaning" -> "var(--rp-ok-text)"
                                "receiving" -> "var(--rp-iris)"
                                else -> "var(--rp-subtle)"
                            }

                            Div({
                                style {
                                    property("display", "flex")
                                    property("justify-content", "space-between")
                                    property("align-items", "center")
                                    property("padding", "1rem")
                                    property("border-radius", "16px")
                                    property("background", if (tmpl.active) "var(--rp-overlay)" else "var(--rp-overlay)")
                                    property("border", "1px solid var(--rp-hl-med)")
                                    property("margin-bottom", "0.5rem")
                                }
                            }) {
                                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.85rem") } }) {
                                    Span({
                                        style {
                                            property("background", catBadgeBg)
                                            property("color", catBadgeFg)
                                            property("padding", "0.25rem 0.7rem")
                                            property("border-radius", "50px")
                                            property("font-size", "0.72rem")
                                            property("font-weight", "700")
                                        }
                                    }) { Text(CAT_LABELS[tmpl.category] ?: tmpl.category) }
                                    Div {
                                        Span({ style { property("font-weight", "800"); property("font-size", "0.98rem"); property("color", "var(--rp-text)") } }) { Text(tmpl.name) }
                                        if (tmpl.category == "temperature" && tmpl.minValue != null && tmpl.maxValue != null) {
                                            Span({ style { property("color", "var(--rp-subtle)"); property("font-size", "0.82rem"); property("margin-left", "0.6rem"); property("font-weight", "600") } }) {
                                                Text("Gräns: ${tmpl.minValue}° – ${tmpl.maxValue}°C")
                                            }
                                        }
                                    }
                                }
                                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.75rem") } }) {
                                    Span({
                                        style {
                                            property("background", "var(--rp-surface)")
                                            property("color", "var(--rp-subtle)")
                                            property("padding", "0.2rem 0.6rem")
                                            property("border-radius", "50px")
                                            property("font-size", "0.72rem")
                                            property("font-weight", "600")
                                            property("border", "1px solid var(--rp-hl-med)")
                                        }
                                    }) { Text(FREQ_LABELS[tmpl.frequency] ?: tmpl.frequency) }

                                    Button({
                                        onClick { fsScope.launch { foodSafetyRepo.updateTemplate(tmpl.id, UpdateFoodSafetyTemplateRequest(active = !tmpl.active)); loadTemplates(); loadToday() } }
                                        style {
                                            property("padding", "0.35rem 0.8rem")
                                            property("border-radius", "50px")
                                            property("border", "none")
                                            property("font-weight", "700")
                                            property("font-size", "0.75rem")
                                            property("cursor", "pointer")
                                            if (tmpl.active) {
                                                property("background", "var(--rp-ok-bg)")
                                                property("color", "var(--rp-ok-text)")
                                            } else {
                                                property("background", "var(--rp-err-bg)")
                                                property("color", "var(--rp-err-text)")
                                            }
                                        }
                                    }) { Text(if (tmpl.active) "Aktiv" else "Inaktiv") }

                                    Button({
                                        onClick { fsScope.launch { foodSafetyRepo.deleteTemplate(tmpl.id); loadTemplates(); loadToday(); feedbackMsg = "✓ Borttagen" } }
                                        style { property("background", "none"); property("border", "none"); property("cursor", "pointer"); property("padding", "0.4rem") }
                                    }) {
                                        FaIcon("fa-solid fa-trash-can", size = "0.9rem", color = "var(--rp-muted)")
                                    }
                                }
                            }
                        }
                    }
                }
            }

            FsTab.LOGG -> {
                Div({
                    style {
                        property("background", "var(--rp-surface)")
                        property("border-radius", "24px")
                        property("padding", "1.5rem")
                        property("border", "1px solid var(--rp-hl-med)")
                        property("box-shadow", "0 4px 15px -2px rgba(0,0,0,0.03)")
                    }
                }) {
                    Div({ style { property("display", "flex"); property("gap", "0.85rem"); property("align-items", "center"); property("margin-bottom", "1.25rem"); property("flex-wrap", "wrap") } }) {
                        Span({ style { property("font-weight", "700"); property("font-size", "0.85rem"); property("color", "var(--rp-subtle)") } }) { Text("Period:") }
                        Input(InputType.Date) { value(logFrom); onInput { logFrom = it.value }; style { fsInput() } }
                        Span({ style { property("color", "var(--rp-muted)") } }) { Text("–") }
                        Input(InputType.Date) { value(logTo); onInput { logTo = it.value }; style { fsInput() } }
                        Button({
                            onClick { loadLogs() }
                            style {
                                property("padding", "0.5rem 1.25rem")
                                property("border", "none")
                                property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-iris))")
                                property("color", "white")
                                property("border-radius", "50px")
                                property("font-weight", "700")
                                property("font-size", "0.82rem")
                                property("cursor", "pointer")
                            }
                        }) { Text("Filtrera logg") }
                    }

                    if (logs.isEmpty()) {
                        Div({ style { property("text-align", "center"); property("padding", "3rem"); property("color", "var(--rp-muted)") } }) {
                            Text("Inga registrerade kontroll-loggar för vald period.")
                        }
                    } else {
                        logs.forEach { log ->
                            val isDev = log.isDeviation
                            Div({
                                style {
                                    property("display", "flex")
                                    property("justify-content", "space-between")
                                    property("align-items", "center")
                                    property("padding", "0.85rem 1rem")
                                    property("border-radius", "14px")
                                    property("margin-bottom", "0.4rem")
                                    property("border", if (isDev) "1px solid var(--rp-err-border)" else "1px solid var(--rp-hl-med)")
                                    property("background", if (isDev) "var(--rp-err-bg)" else "var(--rp-overlay)")
                                }
                            }) {
                                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.85rem") } }) {
                                    Div({
                                        style {
                                            property("width", "36px")
                                            property("height", "36px")
                                            property("border-radius", "50%")
                                            property("background", if (isDev) "var(--rp-err-bg)" else "var(--rp-ok-bg)")
                                            property("display", "flex")
                                            property("align-items", "center")
                                            property("justify-content", "center")
                                        }
                                    }) {
                                        FaIcon("fa-solid ${if (isDev) "fa-triangle-exclamation" else "fa-check"}", size = "0.9rem", color = if (isDev) "var(--rp-err-text)" else "var(--rp-ok-text)")
                                    }
                                    Div {
                                        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                                            Span({ style { property("font-weight", "800"); property("color", "var(--rp-text)"); property("font-size", "0.95rem") } }) { Text(log.templateName) }
                                            if (log.valueNumeric != null) {
                                                Span({
                                                    style {
                                                        property("font-weight", "800")
                                                        property("color", if (isDev) "var(--rp-err-text)" else "var(--rp-ok-text)")
                                                        property("background", if (isDev) "var(--rp-err-bg)" else "var(--rp-ok-bg)")
                                                        property("padding", "0.15rem 0.5rem")
                                                        property("border-radius", "50px")
                                                        property("font-size", "0.8rem")
                                                    }
                                                }) { Text("${log.valueNumeric}°C") }
                                            }
                                            if (log.valueOk != null) {
                                                val ok = log.valueOk == true
                                                Span({
                                                    style {
                                                        property("font-weight", "700")
                                                        property("color", if (ok) "var(--rp-ok-text)" else "var(--rp-err-text)")
                                                        property("background", if (ok) "var(--rp-ok-bg)" else "var(--rp-err-bg)")
                                                        property("padding", "0.15rem 0.5rem")
                                                        property("border-radius", "50px")
                                                        property("font-size", "0.75rem")
                                                    }
                                                }) { Text(if (ok) "OK" else "Ej OK") }
                                            }
                                        }
                                        Span({ style { property("color", "var(--rp-subtle)"); property("font-size", "0.8rem") } }) {
                                            Text("Utförd av ${log.loggedByName}")
                                        }
                                        val da = log.deviationAction
                                        if (isDev && !da.isNullOrBlank()) {
                                            Div({ style { property("color", "var(--rp-err-text)"); property("font-size", "0.8rem"); property("margin-top", "0.2rem"); property("font-weight", "600") } }) {
                                                Text("Åtgärd: $da")
                                            }
                                        }
                                    }
                                }
                                Span({ style { property("color", "var(--rp-muted)"); property("font-size", "0.78rem"); property("font-weight", "600") } }) {
                                    Text(formatFsTime(log.loggedAt))
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    // Modern Create Template Glassmorphic Modal
    if (showCreate) {
        var cName by remember { mutableStateOf("") }
        var cCat by remember { mutableStateOf("temperature") }
        var cUnit by remember { mutableStateOf("C") }
        var cMin by remember { mutableStateOf("") }
        var cMax by remember { mutableStateOf("") }
        var cFreq by remember { mutableStateOf("daily") }
        var cSort by remember { mutableStateOf("0") }
        var cStepRes by remember { mutableStateOf("0.1") }
        var cCustomPresets by remember { mutableStateOf("") }
        var saving by remember { mutableStateOf(false) }

        Div({ onClick { showCreate = false }; style { property("position", "fixed"); property("inset", "0"); property("z-index", "1000"); property("background", "rgba(15, 23, 42, 0.45)"); property("display", "flex"); property("align-items", "center"); property("justify-content", "center"); property("backdrop-filter", "blur(8px)") } }) {
            Div({ onClick { it.stopPropagation() }; style { property("background", "var(--rp-surface)"); property("border-radius", "28px"); property("padding", "2rem 2.25rem"); property("width", "440px"); property("max-width", "90vw"); property("box-shadow", "0 25px 50px -12px rgba(0, 0, 0, 0.25)") } }) {
                H3({ style { property("margin", "0 0 1.5rem 0"); property("font-size", "1.25rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) { Text("Ny kontrollpunkt") }
                Div({ style { property("margin-bottom", "1rem") } }) { Span({ style { fsLabel() } }) { Text("Namn") }; Input(InputType.Text) { value(cName); onInput { cName = it.value }; style { fsModalInput() } } }
                Div({ style { property("display", "grid"); property("grid-template-columns", "1fr 1fr"); property("gap", "1rem"); property("margin-bottom", "1rem") } }) {
                    Div {
                        Span({ style { fsLabel() } }) { Text("Kategori") }
                        Select({ onChange { cCat = it.target.asDynamic().value as String; cUnit = if (cCat == "temperature") "C" else if (cCat == "cleaning") "boolean" else "text" }; attr("value", cCat); style { fsModalInput() } }) {
                            Option("temperature") { Text("Temperatur") }; Option("cleaning") { Text("Rengöring") }
                            Option("receiving") { Text("Mottagning") }; Option("other") { Text("Övrigt") }
                        }
                    }
                    Div {
                        Span({ style { fsLabel() } }) { Text("Frekvens") }
                        Select({ onChange { cFreq = it.target.asDynamic().value as String }; attr("value", cFreq); style { fsModalInput() } }) {
                            Option("daily") { Text("Dagligen") }; Option("per_delivery") { Text("Per leverans") }; Option("weekly") { Text("Veckovis") }
                        }
                    }
                }
                if (cCat == "temperature") {
                    Div({ style { property("display", "grid"); property("grid-template-columns", "1fr 1fr"); property("gap", "1rem"); property("margin-bottom", "1rem") } }) {
                        Div { Span({ style { fsLabel() } }) { Text("Min °C") }; Input(InputType.Number) { value(cMin); onInput { cMin = (it.target.asDynamic().value as? String) ?: "" }; style { fsModalInput() } } }
                        Div { Span({ style { fsLabel() } }) { Text("Max °C") }; Input(InputType.Number) { value(cMax); onInput { cMax = (it.target.asDynamic().value as? String) ?: "" }; style { fsModalInput() } } }
                    }
                    Div({ style { property("display", "grid"); property("grid-template-columns", "1fr 1fr"); property("gap", "1rem"); property("margin-bottom", "1rem") } }) {
                        Div {
                            Span({ style { fsLabel() } }) { Text("Precision") }
                            Select({ onChange { cStepRes = it.target.asDynamic().value as String }; style { fsModalInput() } }) {
                                Option("0.1") { Text("Decimal (0.1 °C)") }
                                Option("1.0") { Text("Heltal (1 °C)") }
                            }
                        }
                        Div {
                            Span({ style { fsLabel() } }) { Text("Egna snabbval (t.ex. 3, 4, 5)") }
                            Input(InputType.Text) {
                                attr("placeholder", "3.0, 4.0, 5.0")
                                value(cCustomPresets)
                                onInput { cCustomPresets = it.value }
                                style { fsModalInput() }
                            }
                        }
                    }
                }
                Div({ style { property("display", "flex"); property("gap", "0.75rem"); property("justify-content", "flex-end"); property("margin-top", "1.75rem") } }) {
                    Button({ onClick { showCreate = false }; style { property("padding", "0.75rem 1.5rem"); property("border", "2px solid var(--rp-overlay)"); property("background", "transparent"); property("color", "var(--rp-subtle)"); property("border-radius", "50px"); property("font-weight", "700"); property("cursor", "pointer") } }) { Text("Avbryt") }
                    Button({
                        onClick {
                            saving = true; fsScope.launch {
                                val customPresets = if (cCat == "temperature" && cCustomPresets.isNotBlank()) cCustomPresets else null
                                val stepRes = if (cStepRes == "1.0") 1.0 else 0.1
                                foodSafetyRepo.createTemplate(
                                    workplaceId,
                                    CreateFoodSafetyTemplateRequest(
                                        name = cName,
                                        category = cCat,
                                        unit = if (cCat == "temperature") "C" else cUnit,
                                        minValue = cMin.toDoubleOrNull(),
                                        maxValue = cMax.toDoubleOrNull(),
                                        frequency = cFreq,
                                        sortOrder = cSort.toIntOrNull() ?: 0,
                                        stepResolution = stepRes,
                                        quickPresets = customPresets
                                    )
                                ).onSuccess { feedbackMsg = "✓ Kontrollpunkt skapad"; showCreate = false; loadTemplates(); loadToday() }
                                saving = false
                            }
                        }; if (saving) disabled()
                        style { property("padding", "0.75rem 1.5rem"); property("border", "none"); property("background", "var(--rp-pine)"); property("color", "white"); property("border-radius", "50px"); property("font-weight", "700"); property("cursor", "pointer") }
                    }) { Text(if (saving) "Sparar…" else "Skapa") }
                }
            }
        }
    }
}

@Composable
private fun FoodSafetyCheckItem(item: FoodSafetyTodayItemDto, workplaceId: Long, onSaved: () -> Unit) {
    val tmpl = item.template
    val isTemp = tmpl.category == "temperature" || tmpl.category == "receiving"
    val isBool = tmpl.unit == "boolean"
    val isDelivery = tmpl.frequency == "per_delivery"

    var valueStr by remember(tmpl.id) { mutableStateOf("") }
    var valueOk by remember(tmpl.id) { mutableStateOf<Boolean?>(null) }
    var actionText by remember(tmpl.id) { mutableStateOf("") }
    var showAction by remember(tmpl.id) { mutableStateOf(false) }
    var saving by remember(tmpl.id) { mutableStateOf(false) }

    // Out of range check
    val numVal = valueStr.toDoubleOrNull()
    val tMin = tmpl.minValue
    val tMax = tmpl.maxValue
    val outOfRange = if (isTemp && numVal != null) {
        (tMin != null && numVal < tMin) || (tMax != null && numVal > tMax)
    } else false

    LaunchedEffect(outOfRange, valueOk) {
        showAction = outOfRange || valueOk == false
    }

    // Quick temperature presets based on min/max or configured presets
    val presets = remember(tMin, tMax, tmpl.quickPresets) {
        val qp = tmpl.quickPresets
        if (!qp.isNullOrBlank()) {
            qp.split(",").map { it.trim() }.filter { it.isNotBlank() }
        } else if (tMin != null && tMax != null) {
            val mid = (tMin + tMax) / 2.0
            val p1 = "${((tMin + 0.5) * 10).toInt() / 10.0}"
            val p2 = "${((mid) * 10).toInt() / 10.0}"
            val p3 = "${((tMax - 0.5) * 10).toInt() / 10.0}"
            listOf(p1, p2, p3)
        } else if (tMax != null) {
            listOf("2.0", "4.0", "6.0")
        } else emptyList()
    }

    val isCleaning = tmpl.category == "cleaning"
    val effectiveBool = isBool || isCleaning

    Div({
        style {
            property("padding", "0.9rem 1.15rem")
            property("border-radius", "14px")
            property("border", if (item.completedToday && !isDelivery) "1px solid var(--rp-hl-med)" else "1px solid var(--rp-hl-med)")
            property("background", if (item.completedToday && !isDelivery) "var(--rp-overlay)" else "var(--rp-surface)")
            property("box-shadow", if (item.completedToday && !isDelivery) "none" else "0 1px 4px rgba(0,0,0,0.02)")
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", "0.6rem")
            property("transition", "all 0.15s ease")
        }
    }) {
        // Completed daily entries view
        if (item.completedToday && !isDelivery) {
            val log = item.logs.firstOrNull()
            Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("align-items", "center") } }) {
                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.75rem") } }) {
                    Div({
                        style {
                            property("width", "28px")
                            property("height", "28px")
                            property("border-radius", "50%")
                            property("background", "var(--rp-ok-bg)")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                        }
                    }) {
                        FaIcon("fa-solid fa-check", size = "0.82rem", color = "var(--rp-ok-text)")
                    }
                    Span({ style { property("font-weight", "800"); property("color", "var(--rp-text)"); property("font-size", "0.92rem") } }) { Text(tmpl.name) }
                    if (log?.valueNumeric != null) {
                        Span({ style { property("font-weight", "800"); property("color", "var(--rp-pine)"); property("background", "var(--rp-ok-bg)"); property("border", "1px solid var(--rp-ok-border)"); property("padding", "0.15rem 0.6rem"); property("border-radius", "50px"); property("font-size", "0.78rem") } }) { Text("${log.valueNumeric}°C") }
                    }
                    if (log?.valueOk != null) {
                        val ok = log.valueOk == true
                        Span({ style { property("font-weight", "700"); property("color", if (ok) "var(--rp-pine)" else "var(--rp-love)"); property("background", if (ok) "var(--rp-ok-bg)" else "var(--rp-err-bg)"); property("border", if (ok) "1px solid var(--rp-ok-border)" else "1px solid var(--rp-err-border)"); property("padding", "0.15rem 0.6rem"); property("border-radius", "50px"); property("font-size", "0.75rem") } }) { Text(if (ok) "Godkänd" else "Ej godkänd") }
                    }
                }
                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem"); property("color", "var(--rp-subtle)"); property("font-size", "0.78rem"); property("font-weight", "600") } }) {
                    Text(log?.loggedByName ?: "")
                    Text("• ${log?.let { formatFsTime(it.loggedAt) } ?: ""}")
                }
            }
            val da2 = log?.deviationAction
            if (log?.isDeviation == true && !da2.isNullOrBlank()) {
                Div({ style { property("color", "var(--rp-err-text)"); property("font-size", "0.78rem"); property("padding-left", "2.2rem"); property("font-weight", "600") } }) { Text("Åtgärd: $da2") }
            }
            return@Div
        }

        // Per-delivery existing logs list
        if (isDelivery && item.logs.isNotEmpty()) {
            item.logs.forEach { log ->
                Div({ style { property("display", "flex"); property("justify-content", "space-between"); property("align-items", "center"); property("padding", "0.45rem 0.75rem"); property("background", if (log.isDeviation) "var(--rp-err-bg)" else "var(--rp-overlay)"); property("border", if (log.isDeviation) "1px solid var(--rp-err-border)" else "1px solid var(--rp-overlay)"); property("border-radius", "10px") } }) {
                    Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                        Span({ style { property("color", if (log.isDeviation) "var(--rp-err-text)" else "var(--rp-ok-text)"); property("font-weight", "800"); property("font-size", "0.82rem") } }) { Text(if (log.valueNumeric != null) "${log.valueNumeric}°C" else if (log.valueOk == true) "Godkänd" else "Ej godkänd") }
                        Span({ style { property("color", "var(--rp-subtle)"); property("font-size", "0.78rem"); property("font-weight", "600") } }) { Text("${log.loggedByName} • ${formatFsTime(log.loggedAt)}") }
                    }
                    val da3 = log.deviationAction
                    if (log.isDeviation && !da3.isNullOrBlank()) {
                        Span({ style { property("color", "var(--rp-err-text)"); property("font-size", "0.75rem"); property("font-weight", "600") } }) { Text("Åtgärd: $da3") }
                    }
                }
            }
        }

        // Interactive Input Row
        Div({ style { property("display", "flex"); property("align-items", "center"); property("justify-content", "space-between"); property("gap", "0.85rem"); property("flex-wrap", "wrap") } }) {
            Div({ style { property("min-width", "180px") } }) {
                Span({ style { property("font-weight", "800"); property("color", "var(--rp-text)"); property("font-size", "0.92rem"); property("display", "block") } }) { Text(tmpl.name) }
                if (tmpl.minValue != null && tmpl.maxValue != null) {
                    Span({ style { property("color", "var(--rp-subtle)"); property("font-size", "0.75rem"); property("font-weight", "600") } }) { Text("Godkänt: ${tmpl.minValue}° – ${tmpl.maxValue}°C") }
                }
            }

            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.55rem"); property("justify-content", "flex-end"); property("flex-wrap", "wrap") } }) {
                // Quick-pick presets for temperatures
                if (isTemp && presets.isNotEmpty() && valueStr.isBlank()) {
                    Div({ style { property("display", "flex"); property("gap", "0.35rem") } }) {
                        presets.forEach { preset ->
                            Button({
                                onClick { valueStr = preset }
                                style {
                                    property("padding", "0.32rem 0.65rem")
                                    property("border-radius", "50px")
                                    property("border", "1px solid var(--rp-hl-med)")
                                    property("background", "var(--rp-overlay)")
                                    property("color", "var(--rp-text)")
                                    property("font-size", "0.76rem")
                                    property("font-weight", "700")
                                    property("cursor", "pointer")
                                    property("font-family", "inherit")
                                    property("transition", "all 0.15s ease")
                                }
                            }) { Text(if (preset.startsWith("-")) "$preset°" else "+$preset°") }
                        }
                    }
                }

                if (isTemp) {
                    Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.35rem") } }) {
                        // Sign toggle
                        Button({
                            onClick {
                                val current = valueStr.toDoubleOrNull() ?: 0.0
                                val toggled = -current
                                valueStr = if (toggled % 1.0 == 0.0 && tmpl.stepResolution >= 1.0) toggled.toInt().toString() else "${((toggled * 10).toInt()) / 10.0}"
                            }
                            style {
                                property("padding", "0.45rem 0.6rem")
                                property("border-radius", "8px")
                                property("border", "1px solid var(--rp-hl-med)")
                                property("background", "var(--rp-overlay)")
                                property("color", "var(--rp-text)")
                                property("font-size", "0.82rem")
                                property("font-weight", "800")
                                property("cursor", "pointer")
                            }
                        }) { Text("±") }

                        // Decrement Button
                        Button({
                            onClick {
                                val step = if (tmpl.stepResolution >= 1.0) 1.0 else 0.1
                                val defaultInitial = if (tMin != null && tMax != null) ((tMin + tMax) / 2.0) else (tMin ?: 0.0)
                                val current = valueStr.toDoubleOrNull() ?: defaultInitial
                                val next = ((current - step) * 10).toInt() / 10.0
                                valueStr = if (tmpl.stepResolution >= 1.0) next.toInt().toString() else "$next"
                            }
                            style {
                                property("width", "32px")
                                property("height", "32px")
                                property("border-radius", "8px")
                                property("border", "1px solid var(--rp-hl-med)")
                                property("background", "var(--rp-surface)")
                                property("color", "var(--rp-text)")
                                property("font-weight", "800")
                                property("cursor", "pointer")
                                property("display", "flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                            }
                        }) { Text("−") }

                        Input(InputType.Number) {
                            value(valueStr); onInput { valueStr = (it.target.asDynamic().value as? String) ?: "" }
                            attr("step", if (tmpl.stepResolution >= 1.0) "1" else "0.1")
                            attr("placeholder", "0.0")
                            style {
                                property("width", "75px")
                                property("padding", "0.45rem 0.5rem")
                                property("border-radius", "8px")
                                property("font-size", "0.95rem")
                                property("font-weight", "800")
                                property("font-family", "inherit")
                                property("outline", "none")
                                property("text-align", "center")
                                property("color", if (outOfRange) "var(--rp-err-text)" else "var(--rp-text)")
                                property("border", if (outOfRange) "2px solid var(--rp-err-border)" else "1.5px solid var(--rp-hl-med)")
                                if (outOfRange) property("background", "var(--rp-err-bg)") else property("background", "var(--rp-surface)")
                            }
                        }

                        // Increment Button
                        Button({
                            onClick {
                                val step = if (tmpl.stepResolution >= 1.0) 1.0 else 0.1
                                val defaultInitial = if (tMin != null && tMax != null) ((tMin + tMax) / 2.0) else (tMin ?: 0.0)
                                val current = valueStr.toDoubleOrNull() ?: defaultInitial
                                val next = ((current + step) * 10).toInt() / 10.0
                                valueStr = if (tmpl.stepResolution >= 1.0) next.toInt().toString() else "$next"
                            }
                            style {
                                property("width", "32px")
                                property("height", "32px")
                                property("border-radius", "8px")
                                property("border", "1px solid var(--rp-hl-med)")
                                property("background", "var(--rp-surface)")
                                property("color", "var(--rp-text)")
                                property("font-weight", "800")
                                property("cursor", "pointer")
                                property("display", "flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                            }
                        }) { Text("+") }

                        Span({ style { property("color", "var(--rp-subtle)"); property("font-weight", "700"); property("font-size", "0.85rem") } }) { Text("°C") }
                    }
                } else if (effectiveBool) {
                    listOf(true to "Godkänd", false to "Avvikelse").forEach { (v, l) ->
                        Button({
                            onClick { valueOk = v }
                            style {
                                property("padding", "0.45rem 0.9rem")
                                property("border-radius", "50px")
                                property("border", if (valueOk == v) "none" else "1px solid var(--rp-hl-med)")
                                property("font-weight", "700")
                                property("font-size", "0.78rem")
                                property("cursor", "pointer")
                                property("font-family", "inherit")
                                property("transition", "all 0.15s ease")
                                if (valueOk == v) {
                                    property("background", if (v) "var(--rp-pine)" else "var(--rp-love)")
                                    property("color", "#FFFFFF")
                                    property("box-shadow", if (v) "0 2px 6px rgba(5,150,105,0.2)" else "0 2px 6px rgba(220,38,38,0.2)")
                                } else {
                                    property("background", "var(--rp-overlay)")
                                    property("color", "var(--rp-subtle)")
                                }
                            }
                        }) {
                            FaIcon(if (v) "fa-solid fa-check" else "fa-solid fa-triangle-exclamation", size = "0.75rem", color = if (valueOk == v) "var(--rp-surface)" else (if (v) "var(--rp-ok-text)" else "var(--rp-err-text)"))
                            Span({ style { property("margin-left", "0.35rem") } }) { Text(l) }
                        }
                    }
                } else {
                    Input(InputType.Text) {
                        value(valueStr); onInput { valueStr = it.value }; attr("placeholder", "Ange värde…")
                        style { property("width", "160px"); property("padding", "0.45rem 0.75rem"); property("border", "1px solid var(--rp-hl-med)"); property("border-radius", "10px"); property("font-size", "0.85rem"); property("outline", "none") }
                    }
                }

                Button({
                    onClick {
                        saving = true
                        fsScope.launch {
                            val req = CreateFoodSafetyLogRequest(tmpl.id, if (isTemp) valueStr.toDoubleOrNull() else null, if (!isTemp && !effectiveBool) valueStr else null, if (effectiveBool) valueOk else null, actionText.ifBlank { null })
                            foodSafetyRepo.submitLog(workplaceId, req).onSuccess { valueStr = ""; valueOk = null; actionText = ""; showAction = false; onSaved() }
                            saving = false
                        }
                    }; if (saving) disabled()
                    style {
                        property("padding", "0.48rem 1.15rem")
                        property("border", "none")
                        property("background", "var(--rp-pine)")
                        property("color", "#FFFFFF")
                        property("border-radius", "50px")
                        property("font-weight", "700")
                        property("font-size", "0.82rem")
                        property("cursor", "pointer")
                        property("font-family", "inherit")
                        property("box-shadow", "0 2px 6px rgba(79, 70, 229, 0.2)")
                        property("display", "flex")
                        property("align-items", "center")
                        property("gap", "0.4rem")
                        property("transition", "all 0.15s ease")
                    }
                }) {
                    FaIcon("fa-solid fa-check", size = "0.75rem", color = "var(--rp-surface)")
                    Text(if (saving) "Sparar…" else "Registrera")
                }
            }
        }

        // Deviation action prompt box
        if (showAction) {
            Div({
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "0.75rem")
                    property("padding", "0.65rem 0.95rem")
                    property("background", "var(--rp-err-bg)")
                    property("border", "1px solid var(--rp-err-border)")
                    property("border-radius", "12px")
                    property("margin-top", "0.2rem")
                }
            }) {
                Span({ style { property("display", "inline-flex"); property("align-items", "center"); property("gap", "0.4rem"); property("color", "var(--rp-err-text)"); property("font-weight", "800"); property("font-size", "0.8rem"); property("white-space", "nowrap") } }) {
                    FaIcon("fa-solid fa-triangle-exclamation", size = "0.85rem", color = "var(--rp-err-text)")
                    Text("Avvikelse upptäckt — åtgärd krävs:")
                }
                Input(InputType.Text) {
                    value(actionText); onInput { actionText = it.value }; attr("placeholder", "Beskriv korrigerande åtgärd (t.ex. justerat termostat, kasserat vara)…")
                    style { property("flex", "1"); property("padding", "0.45rem 0.75rem"); property("border", "1px solid var(--rp-err-border)"); property("border-radius", "8px"); property("font-size", "0.82rem"); property("outline", "none"); property("background", "var(--rp-surface)"); property("font-weight", "500") }
                }
            }
        }
    }
}

private fun todayFs(): String { val d = kotlin.js.Date(); return "${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}" }
private fun addFsDays(days: Int): String { val d = kotlin.js.Date(); (d.asDynamic()).setDate(d.getDate() + days); return "${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}" }
private fun formatFsTime(ms: Long): String { val d = kotlin.js.Date(ms.toDouble()); return "${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}" }

private fun org.jetbrains.compose.web.css.StyleScope.fsLabel() { property("display", "block"); property("font-weight", "700"); property("font-size", "0.72rem"); property("color", "var(--rp-subtle)"); property("text-transform", "uppercase"); property("letter-spacing", "0.5px"); property("margin-bottom", "0.35rem") }
private fun org.jetbrains.compose.web.css.StyleScope.fsInput() { property("padding", "0.5rem 0.75rem"); property("border", "1.5px solid var(--rp-hl-med)"); property("border-radius", "12px"); property("font-size", "0.85rem"); property("font-weight", "600"); property("outline", "none") }
private fun org.jetbrains.compose.web.css.StyleScope.fsModalInput() { property("width", "100%"); property("padding", "0.65rem 0.85rem"); property("border", "1.5px solid var(--rp-hl-med)"); property("border-radius", "14px"); property("font-size", "0.9rem"); property("font-weight", "600"); property("outline", "none"); property("box-sizing", "border-box") }
